import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  FaStethoscope,
  FaGraduationCap, 
  FaChartLine, 
  FaChalkboardTeacher,
  FaBriefcaseMedical,
  FaRegLightbulb,
  FaUsers,
  FaCertificate,
  FaArrowRight,
  FaAngleRight,
  FaInfoCircle,
  FaCheck,
  FaSpinner,
  FaBookMedical,
  FaAward,
  FaFile,
  FaMedal,
  FaClipboardList,
  FaUserMd,
  FaDesktop,
  FaLaptopMedical,
  FaExclamationTriangle,
  FaCompass,
  FaClipboardCheck
} from 'react-icons/fa';
import { getCurrentUser } from 'aws-amplify/auth';
import { generateClient } from 'aws-amplify/api';
import { listUserProfiles } from '../graphql/queries';
import { updateTrainingPathway } from '../api/trainingPathwayApi';
import { saveCategoryPoints } from '../api/categoryPointsApi';

// Import the new API files
import { savePathfinderIMTDomainScore } from '../api/pathfinderIMTDomainScoreApi';
import { savePathfinderCSTDomainScore } from '../api/pathfinderCSTDomainScoreApi';
import { savePathfinderACCSDomainScore } from '../api/pathfinderACCSDomainScoreApi';
import { savePathfinderNeuroSurgDomainScore } from '../api/pathfinderNeuroSurgDomainScoreApi';
import { savePathfinderRadiologyDomainScore } from '../api/pathfinderRadiologyDomainScoreApi'; // Add API import

// Import the new utility
import { getTrainingPathsForSpecialty } from '../utils/trainingPathwayUtils';

// Import actual training pathway data
import IMTData from '../data/PathfinderData/IMT/IMT.json';
import CSTData from '../data/PathfinderData/CST/CST.json';
import ACCSData from '../data/PathfinderData/ACCS/ACCS.json';
import NeurosurgeryData from '../data/PathfinderData/Neurosurgery/NeuroSurg.json'; 
import NeurosurgeryEssentialData from '../data/PathfinderData/Neurosurgery/NeuroSurg_EssentialRequirements.json';
import RadiologyData from '../data/PathfinderData/Radiology/Radiology.json'; // Add Data import

import OnboardingProgressBar from '../components/common/OnboardingProgressBar';

const PathfinderSetup = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [specialty, setSpecialty] = useState('');
  const [trainingPath, setTrainingPath] = useState('');
  const [availablePaths, setAvailablePaths] = useState([]);
  const [showPathSelection, setShowPathSelection] = useState(false);
  const [pathwayData, setPathwayData] = useState(null);
  const [categories, setCategories] = useState([]);
  const [selectedTasks, setSelectedTasks] = useState({});
  const [activeCategories, setActiveCategories] = useState([]); // Changed from activeCategory to support multiple
  const [loading, setLoading] = useState(true);
  const [savingProgress, setSavingProgress] = useState(false);
  const [error, setError] = useState(null);
  const [validationError, setValidationError] = useState(null); // New state for validation errors
  const [userProfile, setUserProfile] = useState(null);
  const [saveError, setSaveError] = useState(null);
  
  // Refs for scrolling to accordion items
  const categoryRefs = useRef({});
  
  // Add debugging state
  const [debugPopup, setDebugPopup] = useState({
    show: false,
    message: '',
    type: 'success',
    details: ''
  });
  const debugTimeout = useRef(null);

  // Map of category names to their icons
  const categoryIcons = {
    "Postgraduate Degrees": FaGraduationCap,
    "Presentations/Posters": FaDesktop,
    "Publications": FaFile,
    "Quality Improvement": FaRegLightbulb,
    "Teaching Experience": FaChalkboardTeacher,
    "Training in Teaching": FaUsers,
    "MRCS": FaBookMedical,
    "Other Qualifications": FaCertificate,
    "Surgical Experience": FaBriefcaseMedical,
    "Surgical Skills": FaLaptopMedical,
    "Audit and Quality Improvement": FaClipboardList,
    "Research": FaChartLine,
    "Teaching": FaChalkboardTeacher,
    "Leadership and Management": FaUserMd,
    "Academia": FaGraduationCap,
    "Additional Achievements": FaMedal
  };
  
  // Default icon for any category not in the mapping
  const DefaultIcon = FaAward;
  
  // Initialize data based on specialty from location state or profile
  useEffect(() => {
    const initializeSetup = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Prefer location.state, fallback to profile.chosenSpecialty
        let selected = location.state?.selectedSpecialty;
        if (!selected) {
          try {
            const user = await getCurrentUser();
            const client = generateClient();
            const { data } = await client.graphql({
              query: listUserProfiles,
              variables: { filter: { owner: { eq: user.username } } },
              authMode: 'userPool'
            });
            const profile = data?.listUserProfiles?.items?.[0];
            if (!profile?.name || !profile?.medicalUniversity || !profile?.yearInMedSchool) {
              navigate('/account_create', { replace: true });
              return;
            }
            setUserProfile(profile);
            selected = profile?.chosenSpecialty || '';
          } catch {
            navigate('/account_create', { replace: true });
            return;
          }
        }

        // If we still don't have a specialty, redirect to Dream Specialty
        if (!selected) {
          navigate('/dream-specialty', { replace: true });
          return;
        }

        // We have a specialty; proceed
        setSpecialty(selected);
        
        // REPLACED: Call the utility function instead of the local one
        const paths = getTrainingPathsForSpecialty(selected);

        setAvailablePaths(paths);
        
        // Show the selection screen if at least one path is available
        if (paths.length > 0) {
          setShowPathSelection(true);
          // Pre-select the first path, but still let the user confirm their choice
          setTrainingPath(paths[0]);
        } else {
          // Default to IMT if no paths are found
          setTrainingPath('IMT');
          loadIMTData();
        }
      } catch (err) {
        console.error('Initialization error:', err);
        // Keep generic error handling for unexpected failures
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    initializeSetup();
  }, [location.state]);
  
  // Fetch user profile on component mount
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const user = await getCurrentUser();
        if (!user) return;
        
        const client = generateClient();
        const { data } = await client.graphql({
          query: listUserProfiles,
          variables: {
            filter: { owner: { eq: user.username } }
          },
          authMode: 'userPool'
        });
        
        if (data.listUserProfiles.items.length > 0) {
          const profile = data.listUserProfiles.items[0];
          setUserProfile(profile);
          
          // If the profile has a saved training path, use it as initial
          if (profile.trainingPath) {
            console.log(`Using saved training path from profile: ${profile.trainingPath}`);
            setTrainingPath(profile.trainingPath);
          }
        }
      } catch (err) {
        console.error('Error fetching user profile:', err);
      }
    };
    
    fetchUserProfile();
  }, []);
  
  // Handle selection of a training path
  const selectTrainingPath = (path) => {
    setTrainingPath(path);
    loadPathwayData(path);
    setShowPathSelection(false);
  };
  
  // Handle toggling an accordion category
  const handleCategoryToggle = (categoryId) => {
    const isActive = activeCategories.includes(categoryId);
    
    if (isActive) {
      // Remove from active categories (Collapse)
      setActiveCategories(prev => prev.filter(id => id !== categoryId));
    } else {
      // Add to active categories (Expand) - allowing multiple open
      setActiveCategories(prev => [...prev, categoryId]);
      
      // Scroll to the opened category with reduced delay
      setTimeout(() => {
        const el = categoryRefs.current[categoryId];
        if (el) {
          // Use scrollIntoView which respects scroll-margin-top set in CSS
          el.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'start' 
          });
        }
      }, 100); // Reduced delay for snappier feel
    }
  };

  // Load pathway data based on selected path
  const loadPathwayData = (path) => {
    switch (path) {
      case 'IMT':
        loadIMTData();
        break;
      case 'CST':
        loadCSTData();
        break;
      case 'ACCS':
        loadACCSData();
        break;
      case 'Neurosurgery':
        loadNeurosurgeryData();
        break;
      case 'Radiology': // Add Case
        loadRadiologyData();
        break;
      default:
        loadIMTData(); // Default to IMT
    }
  };
  
  // Load IMT data
  const loadIMTData = () => {
    setPathwayData(IMTData);
    transformPathwayData(IMTData);
  };
  
  // Load CST data
  const loadCSTData = () => {
    setPathwayData(CSTData);
    transformPathwayData(CSTData);
  };
  
  // Load ACCS data
  const loadACCSData = () => {
    setPathwayData(ACCSData);
    transformPathwayData(ACCSData);
  };

  // Load Neurosurgery data
  const loadNeurosurgeryData = () => {
    // We combine essential requirements (first) with standard requirements
    const essentialCategories = transformDataToCategories(NeurosurgeryEssentialData, true);
    const standardCategories = transformDataToCategories(NeurosurgeryData, false);
    
    // Combine them
    const allCategories = [...essentialCategories, ...standardCategories];
    
    // Update state directly using our new helper
    updateCategoriesState(allCategories);
    
    // Also keep the raw data reference if needed (using standard data as primary)
    setPathwayData(NeurosurgeryData);
  };

  // Add loadRadiologyData function
  const loadRadiologyData = () => {
    // Radiology uses the standard structure (similar to CST/IMT) based on your JSON attachment
    setPathwayData(RadiologyData);
    transformPathwayData(RadiologyData);
  };

  // Helper to transform raw data object into categories array
  const transformDataToCategories = (data, isEssential = false) => {
    if (!data) return [];
    
    return Object.entries(data).map(([categoryName, tasks]) => {
      // Calculate max points, handling nulls/undefined safely
      const pointsArray = tasks.map(task => task["Points Awarded"]);
      // Filter out nulls for calculation, but keep in mind some might be non-graded (0 points)
      const validPoints = pointsArray.filter(p => typeof p === 'number');
      const totalPoints = validPoints.length > 0 ? Math.max(...validPoints) : 0;

      return {
        id: categoryName.replace(/\s+/g, '').replace(/[()]/g, ''),
        name: categoryName,
        description: `${trainingPath} pathway - ${categoryName} assessment`,
        isEssential: isEssential,
        items: tasks.map(task => ({
          id: `${categoryName.replace(/\s+/g, '')}-${task["Task"].replace(/\s+/g, '').replace(/[()]/g, '')}`,
          name: task["Task"],
          points: task["Points Awarded"] || 0, // Default to 0 if null
          requirements: task["Requirements/Notes"],
          evidence: task["Evidence Required"]
        })),
        totalPoints: totalPoints
      };
    });
  };

  // Helper to update state from a list of categories
  const updateCategoriesState = (transformedCategories) => {
    setCategories(transformedCategories);
    
    // Initialize selected tasks object
    const initialSelectedTasks = {};
    transformedCategories.forEach(category => {
      initialSelectedTasks[category.id] = null;
    });
    setSelectedTasks(initialSelectedTasks);
    
    // Set the first category as active
    if (transformedCategories.length > 0) {
      setActiveCategories([transformedCategories[0].id]);
    }
  };

  // Transform the pathway data into the format we need for the UI
  // Maintained for other pathways that use the single-object structure
  const transformPathwayData = (data) => {
    const categories = transformDataToCategories(data, false);
    updateCategoriesState(categories);
  };
  
  // Render the pathway selection UI (re-skinned)
  const renderPathwaySelection = () => {
    return (
      <div className="pfs-pathway-wrap">
        <div className="pfs-hero">
          <div className="pfs-hero-inner">
            <span className="pfs-hero-badge">Pathfinder Setup</span>
            <h1 className="pfs-hero-title">Choose Your Training Pathway</h1>
            <p className="pfs-hero-sub">
              {specialty
                ? `${specialty} can be approached via the following pathways. Pick the one that fits your route if more than 1 is available.`
                : 'Select a pathway to begin personalizing your progress.'}
            </p>
            <div className="pfs-hero-steps" role="list" aria-label="Setup steps">
              
            </div>
          </div>
        </div>

        <div className="pfs-pathways-grid">
          {availablePaths.includes('ACCS') && (
            <button
              type="button"
              className={`pfs-pathway-card ${trainingPath === 'ACCS' ? 'selected' : ''}`}
              onClick={() => selectTrainingPath('ACCS')}
            >
              <div className="pfs-pathway-icon"><FaStethoscope /></div>
              <div className="pfs-pathway-head">
                <h3>ACCS</h3>
                <span className="pfs-pathway-chip">Acute Care Common Stem</span>
              </div>
              <p className="pfs-pathway-copy">
                Emergency, acute medicine, anaesthetics, and ICU exposure to hone broad acute competencies.
              </p>
              <ul className="pfs-pathway-features">
                <li>36 months core training</li>
                <li>Emergency & acute focus</li>
                <li> MSRA Exam required. For EM, final ranking is 40% scaled MSRA and 60% interview score</li>
              </ul>
              <span className="pfs-pathway-cta">Select <FaArrowRight /></span>
            </button>
          )}

          {availablePaths.includes('IMT') && (
            <button
              type="button"
              className={`pfs-pathway-card ${trainingPath === 'IMT' ? 'selected' : ''}`}
              onClick={() => selectTrainingPath('IMT')}
            >
              <div className="pfs-pathway-icon"><FaUserMd /></div>
              <div className="pfs-pathway-head">
                <h3>IMT</h3>
                <span className="pfs-pathway-chip">Internal Medicine Training</span>
              </div>
              <p className="pfs-pathway-copy">
                Broad-based internal medicine training as a foundation to higher specialty training.
              </p>
              <ul className="pfs-pathway-features">
                <li>36 or 24 months programme, depending on Specialty Group</li>
                <li>Medical focus</li>
              </ul>
              <span className="pfs-pathway-cta">Select <FaArrowRight /></span>
            </button>
          )}

          {availablePaths.includes('CST') && (
            <button
              type="button"
              className={`pfs-pathway-card ${trainingPath === 'CST' ? 'selected' : ''}`}
              onClick={() => selectTrainingPath('CST')}
            >
              <div className="pfs-pathway-icon"><FaBriefcaseMedical /></div>
              <div className="pfs-pathway-head">
                <h3>CST</h3>
                <span className="pfs-pathway-chip">Core Surgical Training</span>
              </div>
              <p className="pfs-pathway-copy">
                Two-year programme requiring MSRA exam score of 1200 to be shortlisted for interview. See latest
                requirements{' '}
                <a
                  href="https://medical.hee.nhs.uk/medical-training-recruitment/medical-specialty-training/surgery/core-surgery/core-surgical-training-portfolio-guidance-for-candidates"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  here
                </a>
                .
              </p>
              <ul className="pfs-pathway-features">
                <li>-24 months surgical training</li>
                <li>-Application across 5 domains + MSRA exam</li>
                <li>-MSRA score of ~1200 is the interview-cutoff</li>
              </ul>
              <span className="pfs-pathway-cta">Select <FaArrowRight /></span>
            </button>
          )}
          {availablePaths.includes('Neurosurgery') && (
            <button
              type="button"
              className={`pfs-pathway-card ${trainingPath === 'Neurosurgery' ? 'selected' : ''}`}
              onClick={() => selectTrainingPath('Neurosurgery')}
            >
              <div className="pfs-pathway-icon"><FaBriefcaseMedical /></div>
              <div className="pfs-pathway-head">
                <h3>ST1</h3>
                <span className="pfs-pathway-chip">Neurosurgery Run-Through</span>
              </div>
              {/* THIS IS WHERE THE DESCRIPTION GOES */}
              <p className="pfs-pathway-copy">
                Direct entry run-through training from ST1 to ST8. Highly competitive selection process requiring a specialized portfolio.
              </p>
              <ul className="pfs-pathway-features">
                <li>~8 years run-through training</li>
                <li>Portfolio similar to CST but specific</li>
                <li>Interview + MSRA selection</li>
              </ul>
              <span className="pfs-pathway-cta">Select <FaArrowRight /></span>
            </button>
          )}

          {/* Add Radiology Card */}
          {availablePaths.includes('Radiology') && (
            <button
              type="button"
              className={`pfs-pathway-card ${trainingPath === 'Radiology' ? 'selected' : ''}`}
              onClick={() => selectTrainingPath('Radiology')}
            >
              <div className="pfs-pathway-icon"><FaDesktop /></div>
              <div className="pfs-pathway-head">
                <h3>ST1</h3>
                <span className="pfs-pathway-chip">Clinical Radiology</span>
              </div>
              <p className="pfs-pathway-copy">
                Run-through training starting at ST1. Selection is heavily weighted on the MSRA score and a validated self-assessment portfolio.
              </p>
              <ul className="pfs-pathway-features">
                <li>5-year run-through</li>
                <li>High MSRA cutoff</li>
                <li>Portfolio verification interview</li>
              </ul>
              <span className="pfs-pathway-cta">Select <FaArrowRight /></span>
            </button>
          )}
        </div>
      </div>
    );
  };

  // Select a task for a category (only one task can be selected per category)
  const selectTask = (categoryId, taskId) => {
    // Check if this is an essential category
    const category = categories.find(c => c.id === categoryId);
    if (category && category.isEssential) {
      // For essential categories, we allow multiple selections (checkbox behavior)
      setSelectedTasks(prev => {
        const currentSelected = prev[categoryId] || [];
        // Ensure strictly an array for essential items
        const currentArray = Array.isArray(currentSelected) ? currentSelected : [];
        
        if (currentArray.includes(taskId)) {
          // Uncheck
          return {
            ...prev,
            [categoryId]: currentArray.filter(id => id !== taskId)
          };
        } else {
          // Check
          return {
            ...prev,
            [categoryId]: [...currentArray, taskId]
          };
        }
      });
    } else {
      // Default behavior: Single selection
      setSelectedTasks(prev => ({
        ...prev,
        [categoryId]: taskId
      }));
    }
  };
  
  // Get the selected task for a category
  const getSelectedTask = (categoryId) => {
    return selectedTasks[categoryId];
  };

  // Check if a specific task in a category is selected (helper for rendering)
  const isTaskSelected = (categoryId, taskId) => {
    const selection = selectedTasks[categoryId];
    if (Array.isArray(selection)) {
      return selection.includes(taskId);
    }
    return selection === taskId;
  };
  
  // Calculate the total points and percentage for all categories
  const getOverallProgress = () => {
    if (categories.length === 0) return { percentage: 0, points: 0, totalPoints: 0 };
    
    let selectedPointsSum = 0;
    let totalPointsPossible = 0;
    
    categories.forEach(category => {
      // Skip essential categories for point calculation if they don't have points
      // or handle them differently if needed. For now treating points normally.
      
      const selection = selectedTasks[category.id];
      
      if (Array.isArray(selection)) {
        // Multi-select (Essential)
        selection.forEach(taskId => {
          const item = category.items.find(i => i.id === taskId);
          if (item) selectedPointsSum += (item.points || 0);
        });
        // Total points for essential might be sum of all items? 
        // Usually essential specs are pass/fail (0 points), but if they have points:
        const catTotal = category.items.reduce((sum, item) => sum + (item.points || 0), 0);
        if (category.items.some(i => i.points > 0)) {
           totalPointsPossible += catTotal;
        }
      } else {
        // Single select (Standard)
        if (selection) {
          const selectedItem = category.items.find(item => item.id === selection);
          if (selectedItem) {
            selectedPointsSum += selectedItem.points;
          }
        }
        totalPointsPossible += category.totalPoints;
      }
    });
    
    // Avoid division by zero
    if (totalPointsPossible === 0) return { percentage: 0, points: 0, totalPoints: 0 };
    
    const percentage = Math.round((selectedPointsSum / totalPointsPossible) * 100);
    
    return { percentage, points: selectedPointsSum, totalPoints: totalPointsPossible };
  };
  
  // Calculate points for a specific category
  const getCategoryProgress = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    if (!category) return { points: 0, percentage: 0, totalPoints: 0 };
    
    const selection = selectedTasks[categoryId];
    let points = 0;
    let totalPoints = category.totalPoints;

    if (category.isEssential) {
        // For essential items, progress is "All checked" = 1, otherwise 0
        const checkedCount = Array.isArray(selection) ? selection.length : 0;
        const totalCount = category.items.length;
        
        return {
            points: checkedCount === totalCount ? 1 : 0,
            percentage: totalCount > 0 ? Math.round((checkedCount / totalCount) * 100) : 0,
            totalPoints: 1
        };
    }
    
    if (selection && !Array.isArray(selection)) {
      const selectedItem = category.items.find(item => item.id === selection);
      if (selectedItem) {
        points = selectedItem.points;
      }
    }
    
    const percentage = totalPoints > 0 ? Math.round((points / totalPoints) * 100) : 0;
    
    return { points, percentage, totalPoints };
  };
  
  // Function to show debug popup
  const showDebugPopup = (message, type = 'success', details = '') => {
    if (debugTimeout.current) {
      clearTimeout(debugTimeout.current);
    }
    
    setDebugPopup({
      show: true,
      message,
      type,
      details
    });
    
    debugTimeout.current = setTimeout(() => {
      setDebugPopup(prev => ({ ...prev, show: false }));
    }, 5000);
  };

  // Handle saving progress and navigating to Pathfinder
  const handleContinue = async () => {
    // Reset any previous validation error
    setValidationError(null);

    // VALIDATION: Check essential requirements for Neurosurgery
    if (trainingPath === 'Neurosurgery') {
      const essentialCategories = categories.filter(c => c.isEssential);
      let missingEssentials = false;
      
      for (const cat of essentialCategories) {
        const selected = selectedTasks[cat.id];
        // Must select ALL items in an essential category
        const requiredCount = cat.items.length;
        const selectedCount = Array.isArray(selected) ? selected.length : 0;
        
        if (selectedCount < requiredCount) {
          missingEssentials = true;
          break;
        }
      }
      
      if (missingEssentials) {
        setValidationError("You must verify all Essential Requirements to proceed with a Neurosurgery application.");
        return; 
      }
    }

    try {
      setSavingProgress(true);
      setSaveError(null);
      
      console.log('Starting save process with categories:', categories);
      console.log('Selected tasks:', selectedTasks);
      
      // Save domain scores to their respective tables
      const saveResults = [];
      
      for (const category of categories) {
        const categoryId = category.id;
        const selectedTaskId = selectedTasks[categoryId];
        const categoryName = category.name;
        
        // Validate that we have required data
        if (!categoryName || categoryName.trim() === '') {
          console.warn(`Skipping category with empty name:`, category);
          continue;
        }
        
        // Find the selected task to get its points
        let points = 0;
        
        // Handle Multi-select vs Single-select data saving
        if (Array.isArray(selectedTaskId)) {
             // For essential multi-select categories, points might be sum or 0.
             // Usually these are Pass/Fail. If we need to save scores, sum them up.
             selectedTaskId.forEach(tid => {
                 const t = category.items.find(i => i.id === tid);
                 if (t) points += (t.points || 0);
             });
        } else if (selectedTaskId) {
          const selectedTask = category.items.find(item => item.id === selectedTaskId);
          if (selectedTask) {
            points = selectedTask.points || 0;
          }
        }
        
        console.log(`Saving ${trainingPath} domain score:`, {
          categoryName,
          points,
          trainingPath
        });
        
        try {
          // Save to the appropriate API based on training path - only pass domainKey and points
          let result;
          if (trainingPath === 'IMT') {
            result = await savePathfinderIMTDomainScore(categoryName, points);
          } else if (trainingPath === 'CST') {
            result = await savePathfinderCSTDomainScore(categoryName, points);
          } else if (trainingPath === 'ACCS') {
            result = await savePathfinderACCSDomainScore(categoryName, points);
          } else if (trainingPath === 'Neurosurgery') {
            result = await savePathfinderNeuroSurgDomainScore(categoryName, points);
          } else if (trainingPath === 'Radiology') {
            result = await savePathfinderRadiologyDomainScore(categoryName, points);
          } else {
            console.warn(`Unknown training path: ${trainingPath}`);
            continue;
          }
          
          console.log(`Successfully saved ${categoryName}:`, result);
          saveResults.push({ success: true, categoryName, result });
          
        } catch (saveError) {
          console.error(`Failed to save ${categoryName}:`, saveError);
          saveResults.push({ success: false, categoryName, error: saveError.message });
        }
      }
      
      // Check results
      const successfulSaves = saveResults.filter(r => r.success);
      const failedSaves = saveResults.filter(r => !r.success);
      
      console.log('Save results summary:', {
        total: saveResults.length,
        successful: successfulSaves.length,
        failed: failedSaves.length,
        results: saveResults
      });
      
      // Continue if we have any successful saves (be more lenient)
      if (successfulSaves.length > 0) {
        console.log('Domain scores saved successfully, continuing...');
        
        // Save the training path to the backend
        if (userProfile) {
          try {
            await updateTrainingPathway(userProfile.id, trainingPath);
            console.log(`Training path ${trainingPath} saved to backend`);
          } catch (err) {
            console.warn('Failed to save training path:', err);
            // Don't fail the entire process for this
          }
        }

        // Save the category points to the backend
        try {
          await saveCategoryPoints(trainingPath, categories);
          console.log(`Category points for ${trainingPath} saved to backend`);
        } catch (err) {
          console.warn('Failed to save category points:', err);
          // Don't fail the entire process for this
        }
        
        // Show warning if some saves failed, but still navigate
        if (failedSaves.length > 0) {
          const failedCategories = failedSaves.map(f => f.categoryName).join(', ');
          showDebugPopup(
            `Some categories failed to save: ${failedCategories}`, 
            'warning', 
            `Failed saves: ${JSON.stringify(failedSaves, null, 2)}`
          );
        }
        
        // Navigate to Pathfinder with the data
        navigate('/pathfinder', { 
          state: { 
            fromSetup: true,
            categories: categories,
            specialty: specialty,
            trainingPath: trainingPath
          } 
        });
        
        console.log(`PathfinderSetup navigating to Pathfinder with trainingPath: ${trainingPath}`);
        
      } else {
        // All saves failed
        throw new Error(`Failed to save any domain scores. Errors: ${failedSaves.map(f => f.error).join(', ')}`);
      }
      
    } catch (err) {
      console.error('Error saving setup data:', err);
      const errorMessage = err.message || 'Failed to save your setup data';
      setSaveError(errorMessage);
      showDebugPopup(
        'Failed to save data', 
        'error', 
        `Error: ${errorMessage}\nStack: ${err.stack}\nDetails: ${JSON.stringify(err, null, 2)}`
      );
    } finally {
      setSavingProgress(false);
    }
  };
  
  // Get color for category based on category name
  const getCategoryColor = (categoryId, categoryName) => {
    // Define a color palette based on category names
    const colors = {
      "MRCS": "#3b82f6",           // Blue
      "Postgraduate Degrees": "#8b5cf6", // Purple
      "Academia": "#8b5cf6",       // Purple
      "Presentations/Posters": "#ec4899", // Pink
      "Publications": "#ec4899",    // Pink
      "Research": "#ec4899",       // Pink
      "Quality Improvement": "#10b981", // Green
      "Audit and Quality Improvement": "#10b981", // Green
      "Teaching Experience": "#f59e0b", // Amber
      "Teaching": "#f59e0b",       // Amber
      "Training in Teaching": "#0ea5e9", // Sky blue
      "Surgical Experience": "#ef4444", // Red
      "Surgical Skills": "#ef4444", // Red
      "Leadership and Management": "#64748b", // Slate
      "Other Qualifications": "#6366f1", // Indigo
      "Additional Achievements": "#d946ef" // Fuchsia
    };
    
    // Try to find a color by category name first
    if (colors[categoryName]) {
      return colors[categoryName];
    }
    
    // Fallback to a default color
    return "#6b7280"; // Gray
  };
  
  // Get background color with lower opacity for category
  const getCategoryBgColor = (categoryId, categoryName) => {
    const color = getCategoryColor(categoryId, categoryName);
    
    // Convert hex to rgba with 0.1 opacity
    const hex = color.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    
    return `rgba(${r}, ${g}, ${b}, 0.1)`;
  };
  
  // If still loading, show a loading state
  if (loading) {
    return (
      <div className="pfs-wrap">
        <div className="pfs-loading">
          <FaSpinner className="pfs-spinner" />
          <p>Loading your pathway configuration...</p>
        </div>
      </div>
    );
  }

  // If error, show error state
  if (error) {
    return (
      <div className="pfs-wrap">
        <div className="pfs-error">
          <div className="pfs-error-icon">
            <FaExclamationTriangle />
          </div>
          <h2>There was a problem</h2>
          <p>{error}</p>
          <button 
            className="pfs-error-button"
            onClick={() => navigate('/dream-specialty')}
          >
            Set Your Dream Specialty
          </button>
        </div>
      </div>
    );
  }

  const overallProgress = getOverallProgress();

  return (
    <div className="pfs-wrap">
      <OnboardingProgressBar currentStep={3} />
      {/* Debug popup */}
      {debugPopup.show && (
        <div className={`pfs-debug-popup ${debugPopup.type}`}>
          <div className="pfs-debug-header">
            <span className="pfs-debug-title">{debugPopup.message}</span>
            <button 
              className="pfs-debug-close" 
              onClick={() => setDebugPopup(prev => ({ ...prev, show: false }))}
            >
              ×
            </button>
          </div>
          {debugPopup.details && (
            <div className="pfs-debug-details">
              <pre>{debugPopup.details}</pre>
            </div>
          )}
        </div>
      )}

      {showPathSelection ? (
        renderPathwaySelection()
      ) : (
        <>
          {/* Hero */}
          <div className="pfs-hero">
            <div className="pfs-hero-inner">
              <span className="pfs-hero-badge">Pathfinder Setup</span>
              <h1 className="pfs-hero-title">Configure {trainingPath} for {specialty}</h1>
              <p className="pfs-hero-sub">
                Select your current achievements for each domain to personalize your roadmap.
              </p>
              <div className="pfs-hero-steps" role="list" aria-label="Setup steps">
                <div className="pfs-step"><FaCompass /> Specialty</div>
                <div className="pfs-step"><FaStethoscope /> Pathway</div>
                <div className="pfs-step pfs-step-active"><FaClipboardCheck /> Configure</div>
                <div className="pfs-step"><FaChartLine /> Launch</div>
              </div>
              <div className="pfs-hero-progress">
                <div className="pfs-hero-progress-label">
                  Overall Progress: {overallProgress.points}/{overallProgress.totalPoints} points ({overallProgress.percentage}%)
                </div>
                <div className="pfs-hero-bar">
                  <div className="pfs-hero-fill" style={{ width: `${overallProgress.percentage}%` }} />
                </div>
              </div>
            </div>
          </div>

          {/* Main content - Accordion Layout */}
          <div className="pfs-accordion-container">
            {categories.map(category => {
              const isActive = activeCategories.includes(category.id);
              const { percentage, points, totalPoints } = getCategoryProgress(category.id);
              const Icon = categoryIcons[category.name] || DefaultIcon;
              const selectedTaskId = getSelectedTask(category.id);
              
              return (
                <div 
                  key={category.id} 
                  className={`pfs-accordion-item ${isActive ? 'active' : ''} ${category.isEssential ? 'pfs-essential' : ''}`}
                  ref={el => categoryRefs.current[category.id] = el}
                >
                  <button 
                    className="pfs-accordion-header"
                    onClick={() => handleCategoryToggle(category.id)}
                    aria-expanded={isActive}
                    aria-controls={`content-${category.id}`}
                  >
                    <div className="pfs-cat-icon" style={{ backgroundColor: getCategoryBgColor(category.id, category.name) }}>
                      <Icon style={{ color: getCategoryColor(category.id, category.name) }} />
                    </div>
                    
                    <div className="pfs-cat-content">
                      <div className="pfs-cat-header-top">
                        <span className="pfs-cat-name">{category.name}</span>
                        {category.isEssential && (
                          <span className="pfs-cat-essential-badge">Essential</span>
                        )}
                        {/* Logic for showing selected status badge - check for array or single value */}
                        {((Array.isArray(selectedTaskId) && selectedTaskId.length > 0) || (!Array.isArray(selectedTaskId) && selectedTaskId)) && (
                          <span className="pfs-cat-status-badge">
                            <FaCheck /> {Array.isArray(selectedTaskId) ? `${selectedTaskId.length}/${category.items.length}` : 'Selected'}
                          </span>
                        )}
                      </div>
                      <div className="pfs-cat-progress">
                        <div className="pfs-cat-bar">
                          <div 
                            className="pfs-cat-fill" 
                            style={{ 
                              width: `${percentage}%`,
                              backgroundColor: getCategoryColor(category.id, category.name)
                            }}
                          />
                        </div>
                        <span className="pfs-cat-points">
                             {/* Display points or count depending on type */}
                             {category.isEssential
                                ? `${points}/${totalPoints} Met`
                                : `${points}/${totalPoints} pts`
                             }
                        </span>
                      </div>
                    </div>
                    
                    <div className={`pfs-accordion-chevron ${isActive ? 'rotated' : ''}`}>
                      <FaAngleRight />
                    </div>
                  </button>
                  
                  <div 
                    id={`content-${category.id}`}
                    className={`pfs-accordion-body ${isActive ? 'open' : ''}`}
                    aria-hidden={!isActive}
                  >
                    <div className="pfs-accordion-inner">
                      <div className="pfs-panel-head" style={{ borderColor: getCategoryColor(category.id, category.name) }}>
                        <p>{category.description}</p>
                      </div>

                      <div className="pfs-help">
                        <FaInfoCircle />
                        <span>Select the option that best represents your current achievement level:</span>
                      </div>

                      <div className="pfs-list">
                        {category.items.map(item => {
                          const isSelected = isTaskSelected(category.id, item.id);
                          return (
                          <div 
                            key={item.id} 
                            className={`pfs-item ${isSelected ? 'selected' : ''}`}
                            onClick={() => selectTask(category.id, item.id)}
                          >
                            <div className={`pfs-item-check ${category.isEssential ? 'box' : ''}`}>
                              {isSelected && <FaCheck />}
                            </div>
                            <div className="pfs-item-body">
                              <div className="pfs-item-header">
                                <div className="pfs-item-title">{item.name}</div>
                                { !category.isEssential && <div className="pfs-item-points">{item.points} points</div> }
                              </div>

                              <div className="pfs-item-block">
                                <div className="pfs-block-label">Requirements</div>
                                <div className="pfs-block-text">{item.requirements}</div>
                              </div>

                              <div className="pfs-item-block">
                                <div className="pfs-block-label">Evidence Required</div>
                                <div className="pfs-block-text">{item.evidence}</div>
                              </div>
                            </div>
                          </div>
                        )})}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Action bar (now static at bottom) */}
          <div className="pfs-footer-actions">
            {validationError && (
              <div className="pfs-validation-message">
                <FaExclamationTriangle />
                <span>{validationError}</span>
              </div>
            )}
            <div className="pfs-footer-inner">
              <div className="pfs-progress-summary">
                <span className="pfs-summary-label">Total Progress:</span>
                <span className="pfs-summary-value">{overallProgress.points} / {overallProgress.totalPoints} points</span>
              </div>
              <button 
                className="pfs-cta-btn" 
                onClick={handleContinue}
                disabled={savingProgress}
              >
                {savingProgress ? (
                  <>
                    <FaSpinner className="pfs-spinner" />
                    Saving...
                  </>
                ) : (
                  <>
                    Continue
                    <FaArrowRight />
                  </>
                )}
              </button>
            </div>
            {saveError && <div className="pfs-save-error-footer">{saveError}</div>}
          </div>
        </>
      )}

      <style jsx>{`
        /* Container */
        .pfs-wrap {
          --pfs-bg: #0f172a;
          --pfs-card: #ffffff;
          --pfs-muted: #64748b;
          --pfs-primary: #3b82f6;
          --pfs-primary-2: #60a5fa;
          --pfs-border: #e2e8f0;
          --pfs-ring: rgba(59,130,246,0.15);
          max-width: 900px;
          margin: 0 auto;
          padding: 2rem;
          padding-top: calc(64px + 2rem);
          padding-bottom: 3rem; /* Standard padding */
          min-height: calc(100vh - 64px);
          font-family: Inter, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif;
        }

        /* Hero */
        .pfs-hero {
          position: relative;
          border-radius: 16px;
          padding: 2rem;
          background:
            radial-gradient(700px 220px at 60% -20%, rgba(59,130,246,0.18), transparent),
            linear-gradient(180deg, #eef2ff, #ffffff);
          border: 1px solid rgba(59,130,246,0.15);
          box-shadow: 0 8px 28px rgba(15,23,42,0.06);
          overflow: hidden;
          margin-bottom: 1.75rem;
        }
        .pfs-hero-inner {
          max-width: 840px;
        }
        .pfs-hero-badge {
          display: inline-block;
          padding: 0.35rem 0.6rem;
          font-weight: 700;
          font-size: 0.72rem;
          color: #0b1220;
          background: linear-gradient(90deg, #fce7f3, #dbeafe);
          border: 1px solid rgba(0,0,0,0.06);
          border-radius: 999px;
        }
        .pfs-hero-title {
          margin: 0.6rem 0 0.25rem;
          font-size: clamp(1.6rem, 2.8vw, 2.25rem);
          letter-spacing: -0.02em;
          color: #0f172a;
        }
        .pfs-hero-sub {
          margin: 0.25rem 0 1rem;
          color: var(--pfs-muted);
          max-width: 680px;
        }
        .pfs-hero-steps {
          display: flex;
          gap: 0.5rem;
          flex-wrap: wrap;
          margin-bottom: 1rem;
        }
        .pfs-step {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.4rem 0.7rem;
          border-radius: 999px;
          font-size: 0.8rem;
          background: #f8fafc;
          color: #475569;
          border: 1px solid #e2e8f0;
        }
        .pfs-step svg { color: var(--pfs-primary); }
        .pfs-step-active {
          background: #eff6ff;
          color: #1d4ed8;
          border-color: #bfdbfe;
        }
        .pfs-hero-progress {
          margin-top: 0.5rem;
        }
        .pfs-hero-progress-label {
          font-size: 0.85rem;
          color: #475569;
          margin-bottom: 0.4rem;
          font-weight: 500;
        }
        .pfs-hero-bar {
          height: 8px;
          border-radius: 6px;
          background: #e2e8f0;
          overflow: hidden;
        }
        .pfs-hero-fill {
          height: 100%;
          border-radius: 6px;
          background: linear-gradient(90deg, var(--pfs-primary), var(--pfs-primary-2));
          transition: width 0.3s ease;
        }

        /* Pathway grid */
        .pfs-pathways-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 1rem;
        }
        @media (max-width: 980px) {
          .pfs-pathways-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
        }
        @media (max-width: 640px) {
          .pfs-pathways-grid { grid-template-columns: 1fr; }
        }
        .pfs-pathway-card {
          text-align: left;
          background: var(--pfs-card);
          border: 1px solid var(--pfs-border);
          border-radius: 14px;
          padding: 1rem;
          display: flex;
          flex-direction: column;
          gap: 0.6rem;
          cursor: pointer;
          transition: all 0.2s ease;
          box-shadow: 0 1px 3px rgba(0,0,0,0.04);
        }
        .pfs-pathway-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 24px rgba(2, 6, 23, 0.08);
          border-color: #c7d2fe;
        }
        .pfs-pathway-card.selected {
          border-color: #93c5fd;
          background: linear-gradient(180deg, rgba(59,130,246,0.05), rgba(59,130,246,0.02));
        }
        .pfs-pathway-icon {
          width: 42px;
          height: 42px;
          border-radius: 10px;
          display: grid;
          place-items: center;
          background: #eff6ff;
          color: #2563eb;
          font-size: 1.1rem;
        }
        .pfs-pathway-head {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        .pfs-pathway-head h3 {
          margin: 0;
          font-size: 1.05rem;
          font-weight: 700;
          color: #0f172a;
        }
        .pfs-pathway-chip {
          padding: 0.2rem 0.5rem;
          font-size: 0.7rem;
          border-radius: 999px;
          background: #f1f5f9;
          color: #475569;
          border: 1px solid #e2e8f0;
        }
        .pfs-pathway-copy {
          color: var(--pfs-muted);
          margin: 0.2rem 0 0.2rem;
        }
        .pfs-pathway-features {
          margin: 0.2rem 0 0.4rem;
          padding-left: 1rem;
          color: #475569;
        }
        .pfs-pathway-features li { margin: 0.18rem 0; }
        .pfs-pathway-cta {
          margin-top: auto;
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          color: #1d4ed8;
          font-weight: 600;
        }

        /* Accordion Styles (Replaces Grid) */
        .pfs-accordion-container {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .pfs-accordion-item {
          background: #fff;
          border: 1px solid var(--pfs-border);
          border-radius: 16px;
          overflow: hidden;
          transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
          box-shadow: 0 1px 2px rgba(0,0,0,0.04);
          /* Scroll offset for desktop to account for sticky header */
          scroll-margin-top: 120px;
        }

        .pfs-accordion-item.pfs-essential {
          border: 2px solid #ef4444;
        }

        .pfs-accordion-item:hover {
          box-shadow: 0 4px 12px rgba(0,0,0,0.06);
          border-color: #cbd5e1;
          transform: translateY(-1px);
        }

        .pfs-accordion-item.active {
          border-color: #93c5fd;
          box-shadow: 0 8px 24px rgba(59, 130, 246, 0.1);
          transform: translateY(-2px);
          z-index: 5;
        }

        /* Header */
        .pfs-accordion-header {
          width: 100%;
          display: flex;
          align-items: center;
          gap: 1.25rem;
          padding: 1.25rem 1.5rem;
          background: #fff;
          border: none;
          cursor: pointer;
          text-align: left;
          position: relative;
        }
        
        .pfs-cat-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          display: grid;
          place-items: center;
          font-size: 1.25rem;
          flex-shrink: 0;
        }

        .pfs-cat-content {
          flex: 1;
          min-width: 0;
        }

        .pfs-cat-header-top {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin-bottom: 0.5rem;
        }

        .pfs-cat-name {
          font-weight: 700;
          color: #0f172a;
          font-size: 1.1rem;
        }

        .pfs-cat-essential-badge {
          display: inline-flex;
          align-items: center;
          font-size: 0.7rem;
          font-weight: 700;
          color: #ef4444;
          background: #fef2f2;
          padding: 0.15rem 0.5rem;
          border-radius: 999px;
          border: 1px solid #fee2e2;
          text-transform: uppercase;
        }

        .pfs-cat-status-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.25rem;
          font-size: 0.7rem;
          font-weight: 600;
          color: #10b981;
          background: #ecfdf5;
          padding: 0.15rem 0.5rem;
          border-radius: 999px;
        }

        .pfs-cat-progress {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .pfs-cat-bar {
          flex: 1;
          height: 6px;
          border-radius: 3px;
          background: #f1f5f9;
          overflow: hidden;
        }

        .pfs-cat-fill {
          height: 100%;
          border-radius: 3px;
          transition: width 0.35s ease;
        }

        .pfs-cat-points {
          font-size: 0.8rem;
          color: var(--pfs-muted);
          white-space: nowrap;
          font-weight: 500;
        }

        .pfs-accordion-chevron {
          color: #94a3b8;
          font-size: 1.1rem;
          transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .pfs-accordion-chevron.rotated {
          transform: rotate(90deg);
          color: var(--pfs-primary);
        }

        /* Body */
        .pfs-accordion-body {
          display: grid;
          grid-template-rows: 0fr;
          transition: grid-template-rows 0.35s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .pfs-accordion-body.open {
          grid-template-rows: 1fr;
          border-top: 1px solid #f1f5f9;
        }

        .pfs-accordion-inner {
          overflow: hidden;
          padding: 0 1.5rem 1.5rem;
          background: #fff;
        }

        .pfs-panel-head {
          padding: 1.25rem 0 0.5rem;
        }
        .pfs-panel-head p { 
          color: var(--pfs-muted); 
          margin: 0; 
          font-size: 0.95rem;
          line-height: 1.5;
        }

        .pfs-help {
          display: flex;
          align-items: center;
          gap: 0.6rem;
          color: #64748b;
          margin: 1.25rem 0 1rem;
          font-size: 0.9rem;
          padding: 0.75rem;
          background: #f8fafc;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
        }

        .pfs-list { 
          display: flex; 
          flex-direction: column; 
          gap: 1rem; 
        }

        .pfs-item {
          display: flex;
          gap: 1rem;
          padding: 1.25rem;
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          cursor: pointer;
          transition: all 0.2s ease;
          box-shadow: 0 1px 2px rgba(0,0,0,0.02);
        }

        .pfs-item:hover {
          background: #f8fafc;
          border-color: #cbd5e1;
          transform: translateY(-1px);
        }

        .pfs-item.selected {
          background: #eff6ff;
          border-color: #60a5fa;
          box-shadow: 0 0 0 1px #60a5fa, 0 4px 6px -1px rgba(59, 130, 246, 0.1);
        }

        .pfs-item-check {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          border: 2px solid #cbd5e1;
          display: grid;
          place-items: center;
          color: white;
          font-size: 0.75rem;
          flex-shrink: 0;
          transition: all 0.2s ease;
          margin-top: 0.15rem;
          background: #fff;
        }

        .pfs-item-check.box {
          border-radius: 4px; /* Square for checkbox style */
        }

        .pfs-item.selected .pfs-item-check {
          background: var(--pfs-primary);
          border-color: var(--pfs-primary);
        }

        .pfs-item-body { 
          flex: 1; 
        }

        .pfs-item-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 1rem;
          margin-bottom: 0.75rem;
        }

        .pfs-item-title {
          font-weight: 700;
          color: #0f172a;
          font-size: 1.05rem;
          line-height: 1.4;
        }

        .pfs-item-points {
          font-size: 0.8rem;
          color: var(--pfs-primary);
          font-weight: 700;
          background: #eff6ff;
          padding: 0.25rem 0.6rem;
          border-radius: 6px;
          white-space: nowrap;
        }

        .pfs-item-block { 
          margin-top: 0.75rem; 
        }

        .pfs-block-label {
          font-weight: 600;
          color: #475569;
          margin-bottom: 0.25rem;
          font-size: 0.8rem;
          text-transform: uppercase;
          letter-spacing: 0.02em;
        }

        .pfs-block-text { 
          color: #64748b; 
          line-height: 1.6;
          font-size: 0.95rem; 
        }

        /* Footer Actions */
        .pfs-footer-actions {
          margin-top: 2rem;
          background: #ffffff;
          border: 1px solid var(--pfs-border);
          border-radius: 16px;
          padding: 1.5rem;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
        }

        .pfs-footer-inner {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .pfs-progress-summary {
          display: flex;
          flex-direction: column;
        }

        .pfs-summary-label {
          font-size: 0.85rem;
          color: #64748b;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.02em;
        }

        .pfs-summary-value {
          font-size: 1.35rem;
          font-weight: 700;
          color: #0f172a;
          margin-top: 0.1rem;
        }

        .pfs-cta-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.6rem;
          justify-content: center;
          background: var(--pfs-primary);
          color: #fff;
          border: 1px solid rgba(0,0,0,0.06);
          padding: 0.9rem 2rem;
          border-radius: 12px;
          font-weight: 600;
          font-size: 1rem;
          cursor: pointer;
          transition: all 0.2s ease;
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.25);
        }

        .pfs-cta-btn:hover {
          background: #2563eb;
          transform: translateY(-2px);
          box-shadow: 0 8px 18px rgba(59, 130, 246, 0.3);
        }

        .pfs-cta-btn:disabled {
          background: #94a3b8;
          cursor: not-allowed;
          transform: none;
          box-shadow: none;
        }

        .pfs-save-error-footer {
          text-align: center;
          color: #ef4444;
          font-size: 0.85rem;
          margin-top: 1rem;
          padding: 0.5rem;
          background: #fef2f2;
          border-radius: 6px;
        }

        .pfs-validation-message {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.75rem;
          background-color: #fef2f2;
          border: 1px solid #fee2e2;
          color: #ef4444;
          padding: 1rem;
          border-radius: 8px;
          margin-bottom: 1.5rem;
          font-weight: 600;
          font-size: 0.95rem;
          text-align: center;
          animation: shake 0.5s cubic-bezier(.36,.07,.19,.97) both;
        }

        @keyframes shake {
          10%, 90% { transform: translate3d(-1px, 0, 0); }
          20%, 80% { transform: translate3d(2px, 0, 0); }
          30%, 50%, 70% { transform: translate3d(-4px, 0, 0); }
          40%, 60% { transform: translate3d(4px, 0, 0); }
        }

        /* Responsive */
        @media (max-width: 768px) {
          .pfs-wrap {
            padding: 1rem;
            padding-top: calc(64px + 1rem);
            padding-bottom: 2rem;
          }
          
          /* Adjust scroll offset for mobile */
          .pfs-accordion-item {
            scroll-margin-top: 90px;
          }
          
          .pfs-accordion-header {
            padding: 1rem;
            gap: 0.85rem;
          }
          
          .pfs-cat-icon {
            width: 40px;
            height: 40px;
            font-size: 1.1rem;
          }

          .pfs-cat-name {
            font-size: 1rem;
          }

          .pfs-accordion-inner {
            padding: 0 1rem 1rem;
          }

          .pfs-item {
            padding: 1rem;
            flex-direction: column;
            gap: 0.75rem;
          }

          .pfs-item-check {
            align-self: flex-start;
            margin-top: 0;
          }
          
          /* Reduce font sizes inside accordion for mobile */
          .pfs-panel-head p,
          .pfs-help,
          .pfs-block-text,
          .pfs-item-title,
          .pfs-item-points {
            font-size: 0.85rem;
          }

          .pfs-block-label {
            font-size: 0.75rem;
          }

          .pfs-help {
            padding: 0.6rem;
            margin: 1rem 0 0.75rem;
          }
          
          .pfs-footer-inner {
            flex-direction: column;
            gap: 1.25rem;
            text-align: center;
          }
          
          .pfs-cta-btn {
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

export default PathfinderSetup;
